import React, { useState, useMemo } from 'react';
import { useEffect, useCallback } from 'react';
import {
  Plus, Calendar, Phone, Mail, FileText, Clock, User, Filter,
  CreditCard as Edit, Trash2, Search, X, Paperclip, AlertTriangle,
  TrendingUp, Building2, ChevronDown, ChevronRight, ExternalLink
} from 'lucide-react';
import { activitiesService, contactsService } from '../services/supabaseService';
import { Activity } from '../types';
import ActivityModal from './ActivityModal';
import ActivityAttachmentsViewer from './ActivityAttachmentsViewer';
import { activityAttachmentsService } from '../services/activityAttachmentsService';
import { useNotifications } from '../hooks/useNotifications';
import { useDataLoader } from '../hooks/useDataLoader';
import { useDataSync } from '../contexts/DataSyncContext';

const ACTIVITY_CONFIG: Record<string, { label: string; icon: typeof Calendar; color: string; bg: string }> = {
  reunion: { label: 'Reunión', icon: Calendar, color: 'text-orange-600', bg: 'bg-orange-100' },
  llamada: { label: 'Llamada', icon: Phone, color: 'text-blue-600', bg: 'bg-blue-100' },
  correo: { label: 'Correo', icon: Mail, color: 'text-green-600', bg: 'bg-green-100' },
  nota_interna: { label: 'Nota Interna', icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
};

function formatDateGroup(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (d.getTime() === today.getTime()) return 'Hoy';
  if (d.getTime() === yesterday.getTime()) return 'Ayer';

  const diffDays = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diffDays < 7) return `Hace ${diffDays} días`;

  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

const ActivityList: React.FC = () => {
  const { notifyActivityCreated } = useNotifications();
  const { notifyDataChange } = useDataSync();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewingAttachmentsFor, setViewingAttachmentsFor] = useState<string | null>(null);
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  const { data: activities, loading: loadingActivities, refetch: refetchActivities } = useDataLoader<Activity[]>(
    'activities-list',
    () => activitiesService.getAll(),
    {
      ttl: 60,
      realtimeTable: 'activities',
      realtimeEnabled: true,
      staleWhileRevalidate: true,
      onRealtimeEvent: (event) => {
        notifyDataChange('activities', event.type === 'INSERT' ? 'create' : event.type === 'UPDATE' ? 'update' : 'delete', event.record?.id);
      }
    }
  );

  const { data: contacts } = useDataLoader<any[]>(
    'activities-contacts',
    () => contactsService.getAll(),
    { ttl: 60, realtimeTable: 'contacts', realtimeEnabled: true }
  );

  const loadData = useCallback(async () => {
    await refetchActivities();
  }, [refetchActivities]);

  // Filtered and sorted activities
  const sortedActivities = useMemo(() => {
    const filtered = (activities || []).filter(activity => {
      const matchesType = filter === 'all' || activity.type === filter;
      const matchesOwner = ownerFilter === 'all' || activity.owner === ownerFilter;
      const matchesSearch = !searchTerm ||
        activity.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.contactCompany?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.leadName?.toLowerCase().includes(searchTerm.toLowerCase());

      const actDate = new Date(activity.datetime);
      const matchesDateFrom = !dateFrom || actDate >= new Date(dateFrom);
      const matchesDateTo = !dateTo || actDate <= new Date(dateTo + 'T23:59:59');

      return matchesType && matchesOwner && matchesSearch && matchesDateFrom && matchesDateTo;
    });

    return filtered.sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime());
  }, [activities, filter, ownerFilter, searchTerm, dateFrom, dateTo]);

  // Group by day
  const groupedActivities = useMemo(() => {
    const groups: { key: string; label: string; date: Date; activities: Activity[] }[] = [];
    const map = new Map<string, Activity[]>();

    for (const act of sortedActivities) {
      const d = new Date(act.datetime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(act);
    }

    for (const [key, acts] of map) {
      groups.push({
        key,
        label: formatDateGroup(acts[0].datetime),
        date: new Date(acts[0].datetime),
        activities: acts,
      });
    }

    return groups;
  }, [sortedActivities]);

  // Stats
  const stats = useMemo(() => {
    const all = activities || [];
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 86400000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const thisWeek = all.filter(a => new Date(a.datetime) >= weekAgo).length;
    const thisMonth = all.filter(a => new Date(a.datetime) >= monthStart).length;

    // Clients without activity in >7 days
    const clientLastActivity = new Map<string, Date>();
    for (const a of all) {
      if (!a.contactId) continue;
      const prev = clientLastActivity.get(a.contactId);
      const d = new Date(a.datetime);
      if (!prev || d > prev) clientLastActivity.set(a.contactId, d);
    }

    const inactiveClients: { id: string; name: string; company: string; lastActivity: Date; daysAgo: number }[] = [];
    for (const [clientId, lastDate] of clientLastActivity) {
      const daysAgo = Math.floor((now.getTime() - lastDate.getTime()) / 86400000);
      if (daysAgo >= 7) {
        const contact = (contacts || []).find(c => c.id === clientId);
        if (contact) {
          inactiveClients.push({
            id: clientId,
            name: contact.fullName,
            company: contact.company || '',
            lastActivity: lastDate,
            daysAgo,
          });
        }
      }
    }
    inactiveClients.sort((a, b) => b.daysAgo - a.daysAgo);

    return {
      total: all.length,
      thisWeek,
      thisMonth,
      byType: {
        reunion: all.filter(a => a.type === 'reunion').length,
        llamada: all.filter(a => a.type === 'llamada').length,
        correo: all.filter(a => a.type === 'correo').length,
        nota_interna: all.filter(a => a.type === 'nota_interna').length,
      },
      inactiveClients,
    };
  }, [activities, contacts]);

  const uniqueOwners = useMemo(() =>
    Array.from(new Set((activities || []).map(a => a.owner))).filter(Boolean),
    [activities]
  );

  const toggleDay = (key: string) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleCreateActivity = () => {
    setEditingActivity(null);
    setIsModalOpen(true);
  };

  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    setIsModalOpen(true);
  };

  const handleSaveActivity = async (activityData: any) => {
    try {
      const attachments = activityData.attachments || [];
      delete activityData.attachments;

      if (editingActivity) {
        await activitiesService.update(editingActivity.id, activityData);
        if (attachments.length > 0) {
          await activityAttachmentsService.uploadMultipleFiles(editingActivity.id, attachments, activityData.owner);
        }
        alert('Actividad actualizada correctamente');
      } else {
        const newActivity = await activitiesService.create({
          type: activityData.type || 'reunion',
          datetime: activityData.datetime || new Date(),
          description: activityData.description || '',
          contactId: activityData.contactId,
          leadId: activityData.leadId,
          owner: activityData.owner || ''
        });

        if (attachments.length > 0) {
          await activityAttachmentsService.uploadMultipleFiles(newActivity.id, attachments, activityData.owner);
        }

        const contact = (contacts || []).find(c => c.id === activityData.contactId);
        await notifyActivityCreated(
          ACTIVITY_CONFIG[activityData.type || 'reunion']?.label || 'Actividad',
          contact?.fullName || 'Cliente',
          undefined
        );

        notifyDataChange('activities', 'create', newActivity.id);
      }

      setIsModalOpen(false);
      setEditingActivity(null);
    } catch (error) {
      console.error('Error saving activity:', error);
      alert('Error al guardar la actividad');
    }
  };

  const handleDeleteActivity = async (activityId: string) => {
    const activity = (activities || []).find(a => a.id === activityId);
    if (!activity) return;

    const msg = activity.attachmentCount && activity.attachmentCount > 0
      ? `¿Eliminar esta actividad y sus ${activity.attachmentCount} adjunto(s)?`
      : '¿Eliminar esta actividad?';

    if (!window.confirm(msg)) return;

    try {
      if (activity.attachmentCount && activity.attachmentCount > 0) {
        await activityAttachmentsService.deleteAttachmentsByActivity(activityId).catch(() => {});
      }
      await activitiesService.delete(activityId);
      notifyDataChange('activities', 'delete', activityId);
    } catch (error: any) {
      alert(error.message || 'Error al eliminar la actividad');
    }
  };

  const clearFilters = () => {
    setFilter('all');
    setOwnerFilter('all');
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = filter !== 'all' || ownerFilter !== 'all' || searchTerm || dateFrom || dateTo;

  if (loadingActivities) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando actividades...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Actividades</h1>
          <p className="text-gray-600">Historial completo de interacciones con contactos</p>
        </div>
        <button
          onClick={handleCreateActivity}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nueva Actividad</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Clock className="w-7 h-7 text-blue-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase">Esta Semana</p>
              <p className="text-2xl font-bold text-gray-900">{stats.thisWeek}</p>
            </div>
            <TrendingUp className="w-7 h-7 text-green-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase">Este Mes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.thisMonth}</p>
            </div>
            <Calendar className="w-7 h-7 text-orange-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-4 gap-1 text-center">
            {Object.entries(ACTIVITY_CONFIG).map(([key, cfg]) => (
              <div key={key}>
                <p className="text-xs text-gray-400">{cfg.label.slice(0, 3)}</p>
                <p className={`text-lg font-bold ${cfg.color}`}>{stats.byType[key as keyof typeof stats.byType] || 0}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-4 ${
          stats.inactiveClients.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase">Sin actividad +7d</p>
              <p className={`text-2xl font-bold ${stats.inactiveClients.length > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                {stats.inactiveClients.length}
              </p>
            </div>
            <AlertTriangle className={`w-7 h-7 ${stats.inactiveClients.length > 0 ? 'text-amber-400' : 'text-gray-300'}`} />
          </div>
        </div>
      </div>

      {/* Inactive clients alert */}
      {stats.inactiveClients.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900">Clientes sin actividad reciente</h3>
              <p className="text-xs text-amber-700 mb-2">Estos clientes no tienen interacciones en los últimos 7+ días:</p>
              <div className="flex flex-wrap gap-2">
                {stats.inactiveClients.slice(0, 8).map(c => (
                  <span key={c.id} className="inline-flex items-center space-x-1 bg-white border border-amber-300 rounded-full px-3 py-1 text-xs">
                    <User className="w-3 h-3 text-amber-600" />
                    <span className="font-medium text-gray-800">{c.name}</span>
                    <span className="text-amber-600">({c.daysAgo}d)</span>
                  </span>
                ))}
                {stats.inactiveClients.length > 8 && (
                  <span className="text-xs text-amber-600 self-center">+{stats.inactiveClients.length - 8} más</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 space-y-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por descripción, contacto o empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-500 uppercase">Tipo:</span>
            <div className="flex gap-1">
              <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>Todas</FilterPill>
              {Object.entries(ACTIVITY_CONFIG).map(([key, cfg]) => (
                <FilterPill key={key} active={filter === key} onClick={() => setFilter(key)}>{cfg.label}s</FilterPill>
              ))}
            </div>
          </div>

          <div className="h-5 w-px bg-gray-200" />

          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-xs border border-gray-300 rounded-lg px-2 py-1.5"
              placeholder="Desde"
            />
            <span className="text-xs text-gray-400">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-xs border border-gray-300 rounded-lg px-2 py-1.5"
            />
          </div>

          {uniqueOwners.length > 1 && (
            <>
              <div className="h-5 w-px bg-gray-200" />
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-400" />
                <select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  className="text-xs border border-gray-300 rounded-lg px-2 py-1.5"
                >
                  <option value="all">Todos</option>
                  {uniqueOwners.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </>
          )}

          {hasActiveFilters && (
            <>
              <div className="h-5 w-px bg-gray-200" />
              <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                Limpiar filtros
              </button>
              <span className="text-xs text-gray-400">
                {sortedActivities.length} de {(activities || []).length}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Timeline grouped by day */}
      <div className="space-y-4">
        {groupedActivities.length > 0 ? (
          groupedActivities.map(group => {
            const isCollapsed = collapsedDays.has(group.key);

            return (
              <div key={group.key} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Day header */}
                <button
                  onClick={() => toggleDay(group.key)}
                  className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {isCollapsed
                      ? <ChevronRight className="w-4 h-4 text-gray-400" />
                      : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    <span className="text-sm font-semibold text-gray-700 capitalize">{group.label}</span>
                    <span className="text-xs text-gray-400">
                      {group.date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Type badges for this day */}
                    {Object.entries(ACTIVITY_CONFIG).map(([key, cfg]) => {
                      const count = group.activities.filter(a => a.type === key).length;
                      if (!count) return null;
                      const Icon = cfg.icon;
                      return (
                        <span key={key} className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs ${cfg.bg} ${cfg.color}`}>
                          <Icon className="w-3 h-3" />
                          <span>{count}</span>
                        </span>
                      );
                    })}
                    <span className="text-xs text-gray-400 ml-1">{group.activities.length} total</span>
                  </div>
                </button>

                {/* Activities for this day */}
                {!isCollapsed && (
                  <div className="divide-y divide-gray-100">
                    {group.activities.map(activity => {
                      const cfg = ACTIVITY_CONFIG[activity.type] || ACTIVITY_CONFIG.nota_interna;
                      const Icon = cfg.icon;

                      return (
                        <div key={activity.id} className="px-5 py-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start space-x-3">
                            <div className={`p-2 rounded-lg ${cfg.bg} flex-shrink-0 mt-0.5`}>
                              <Icon className={`w-4 h-4 ${cfg.color}`} />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                                  <span className="text-sm font-medium text-gray-900">{cfg.label}</span>
                                  <span className="text-xs text-gray-400">
                                    {activity.datetime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  {activity.owner && (
                                    <span className="text-xs text-gray-500 flex items-center space-x-1">
                                      <User className="w-3 h-3" />
                                      <span>{activity.owner}</span>
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1 flex-shrink-0">
                                  {activity.attachmentCount && activity.attachmentCount > 0 && (
                                    <button
                                      onClick={() => setViewingAttachmentsFor(activity.id)}
                                      className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                                    >
                                      <Paperclip className="w-3 h-3" />
                                      <span>{activity.attachmentCount}</span>
                                    </button>
                                  )}
                                  <button onClick={() => handleEditActivity(activity)} className="text-gray-400 hover:text-blue-600 p-1 rounded">
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => handleDeleteActivity(activity.id)} className="text-gray-400 hover:text-red-600 p-1 rounded">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Client/Lead info */}
                              {(activity.contactName || activity.leadName) && (
                                <div className="flex items-center space-x-3 mt-1 text-xs">
                                  {activity.contactName && (
                                    <span className="flex items-center space-x-1 text-blue-600">
                                      <Building2 className="w-3 h-3" />
                                      <span className="font-medium">{activity.contactName}</span>
                                      {activity.contactCompany && (
                                        <span className="text-gray-400">({activity.contactCompany})</span>
                                      )}
                                    </span>
                                  )}
                                  {activity.leadName && (
                                    <span className="flex items-center space-x-1 text-purple-600">
                                      <TrendingUp className="w-3 h-3" />
                                      <span className="font-medium">{activity.leadName}</span>
                                      {activity.leadCompany && (
                                        <span className="text-gray-400">({activity.leadCompany})</span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              )}

                              {activity.description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{activity.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-12">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay actividades</h3>
            <p className="text-gray-500 mb-6">
              {hasActiveFilters
                ? 'No se encontraron actividades con los filtros aplicados'
                : 'Comienza registrando tu primera actividad'}
            </p>
            {!hasActiveFilters && (
              <button onClick={handleCreateActivity} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mx-auto">
                <Plus className="w-4 h-4" />
                <span>Nueva Actividad</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Activity Modal */}
      <ActivityModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingActivity(null); }}
        onSave={handleSaveActivity}
        activity={editingActivity}
      />

      {/* Attachments Viewer */}
      {viewingAttachmentsFor && (
        <ActivityAttachmentsViewer
          activityId={viewingAttachmentsFor}
          onClose={() => setViewingAttachmentsFor(null)}
          onDelete={() => loadData()}
        />
      )}
    </div>
  );
};

// Filter pill button
const FilterPill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
      active ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
    }`}
  >
    {children}
  </button>
);

export default ActivityList;
